import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import BaseCcmCampaignPage from '../../BaseCcmCampaignPage';
import CcmCampaignOverview from 'modules/organizations/components/ccm-campaign-overview/CcmCampaignOverview';
import CcmCampaignOffers from 'modules/campaigns/components/ccm/components/offers/CcmCampaignOffers';
import CcmCampaignTimeline from 'modules/campaigns/components/ccm/components/timeline/CcmCampaignTimeline';
import CcmCampaignMainInfo from 'modules/organizations/components/ccm-campaign-overview/CcmCampaignMainInfo';
import CcmCreatorlessOutfits from 'modules/organizations/components/ccm-creatorless-outfits/CcmCreatorlessOutfits';

import { __t, applyTranslations } from 'utils/language';
import { isCcmModerator } from 'utils/account';

import './ccm-campaign-overview-page.scss';

applyTranslations(require('./ccm-campaign-overview-page.i18n.json'));

import type {
  Campaign,
  Params,
  Role
} from 'types';

type OwnProps = {
  params: Params
};

type StateProps = {
  campaign: Campaign | undefined,
  roles: Array<Role>
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'ccm_campaign_overview_page';

const CcmCampaignOverviewPage: React.FC<Props> = ({
  params,
  campaign,
  roles
}): JSX.Element => {
  const isModerator = isCcmModerator(roles);
  const isCampaignCreatorless = campaign?.campaignType === 'ccm_creatorless';
  const hasOffers = isModerator && !isCampaignCreatorless && !isNil(campaign?.campaignType);

  React.useEffect(() => {
    // manually scroll to top when applicable (global scroll to top in `src/index.tsx` is disabled for these pages).
    // this manual-handling is done to prevent the scroll to top when clicking the offer tabs and offer rows.
    if (!window.location.href.includes('clicked=') && isEmpty(window.location.hash)) {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="ccm-campaign-overview-page">
      <BaseCcmCampaignPage
        params={params}
        campaignType={campaign?.campaignType ?? 'ccm'}
        documentTitle={__t(`${MSG_KEY}.document_title`)}
        pageType="overview"
        childrenClassName="ccm-campaign-overview-page__base"
      >
        {
          !isCampaignCreatorless ? (
            <React.Fragment>
              <CcmCampaignTimeline
                timeline={campaign?.timeline ?? null}
              />
              <CcmCampaignOverview
                campaign={campaign}
                isCcmModerator={isModerator}
              />
            </React.Fragment>
          ) : (
            <div className="ccm-campaign-overview-page__overview-info">
              <CcmCampaignTimeline
                timeline={campaign?.timeline ?? null}
                isCampaignCreatorless={isCampaignCreatorless}
              />
              <div className="ccm-campaign-overview-page__info-container">
                <CcmCampaignMainInfo
                  campaign={campaign}
                  isCampaignCreatorless={isCampaignCreatorless}
                />
              </div>
            </div>
          )
        }
        {
          hasOffers && (
            <CcmCampaignOffers
              params={params}
            />
          )
        }
        {
          isCampaignCreatorless && (
            <CcmCreatorlessOutfits isModerator={isModerator} />
          )
        }
      </BaseCcmCampaignPage>
    </div>
  );
};

const mapStateToProps = ({ account, campaignBrief }): StateProps => ({
  campaign: campaignBrief.brief,
  roles: account.roles
});

export default connect(mapStateToProps)(CcmCampaignOverviewPage);
